import { AppState, Auth0Provider } from '@auth0/auth0-react'
import { ReactNode, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import { useAuthStore } from '../stores/authStore'
import { AuthConfigProvider } from './AuthConfigContext'
import { ProfileProvider } from './ProfileProvider'

export interface AuthProviderProps {
  children: ReactNode
  domain: string
  mfaDomain: string
  audience: string
  clientId: string
  redirectUri: string
  isOnRedirectorApp?: boolean
  basename?: string
}

export function AuthProvider({
  children,
  domain,
  mfaDomain,
  audience,
  clientId,
  redirectUri,
  isOnRedirectorApp = false,
  basename,
}: AuthProviderProps) {
  const navigate = useNavigate()

  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      const returnTo = appState?.returnTo

      if (isOnRedirectorApp) {
        const state = returnTo !== '/' ? { returnTo } : undefined

        return navigate('/redirect', { replace: true, state })
      }

      const withMFAScopes = appState?.withMFAScopes
      returnTo && navigate(returnTo, { replace: true, state: { hasRedirectedFromAuth: true, withMFAScopes } })
    },
    [isOnRedirectorApp, navigate]
  )

  const setBasename = useAuthStore((state) => state.setBasename)
  useEffect(() => {
    basename && setBasename(basename)
  }, [basename, setBasename])

  return (
    <AuthConfigProvider
      domain={domain}
      mfaDomain={mfaDomain}
      audience={audience}
      clientId={clientId}
      redirectUri={redirectUri}
    >
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        redirectUri={redirectUri}
        audience={audience}
        onRedirectCallback={onRedirectCallback}
        cacheLocation='localstorage'
      >
        <ProfileProvider>{children}</ProfileProvider>
      </Auth0Provider>
    </AuthConfigProvider>
  )
}
